import React, { Component } from "react";
import { AxiosError, AxiosResponse } from "axios";
import SignUpForm from "./SignUpForm";
import LoginForm from "./LoginForm";
import LinkedinIcon from "./LinkedinIcon";
import FacebookIcon from "./FacebookIcon";
import GoogleIcon from "./GoogleIcon";
import { postRequest } from "../../utils/httpRequest";

interface Props {
  currentUser: { kind: string };
}

interface State {
  mode: "email" | "login" | "signUp";
  email: string;
  error: string;
  lookingUp: boolean;
  showHelp: boolean;
}

interface LookupResponse {
  exists: boolean;
}

class Login extends Component<Props, State> {
  helpWrapRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      mode: "email",
      email: "",
      error: "",
      lookingUp: false,
      showHelp: false,
    };
  }

  componentDidMount() {
    document.body.classList.add("auth-login-page-active");
    document.addEventListener("mousedown", this.handleOutsideHelpClick);
  }

  componentWillUnmount() {
    document.body.classList.remove("auth-login-page-active");
    document.removeEventListener("mousedown", this.handleOutsideHelpClick);
  }

  closeError = (e) => {
    e.preventDefault();

    this.setState({ error: "" });
  };

  setError = (error) => {
    this.setState({ error });
    setTimeout(() => {
      this.setState({ error: "" });
    }, 10000);
  };

  setEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: e.target.value, error: "" });
  };

  handleEmailLookup = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = this.state.email.trim();

    if (!email) {
      this.setError("Please enter your email first.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setError("Please enter a valid email address.");
      return;
    }

    this.setState({ lookingUp: true, error: "", email });

    postRequest<LookupResponse, { session: { email: string } }>(
      "/session/email/lookup.json",
      {
        session: { email },
      },
    )
      .then((response: AxiosResponse<LookupResponse>) => {
        this.setState({
          mode: response.data.exists ? "login" : "signUp",
          lookingUp: false,
        });
      })
      .catch((error: AxiosError<{ errors?: string }>) => {
        this.setState({
          lookingUp: false,
          error:
            error.response?.data?.errors ||
            "Sorry, we could not check that email. Try again.",
        });
      });
  };

  handleEditEmail = () => {
    this.setState({ mode: "email", error: "" });
  };

  toggleHelp = () => {
    this.setState(({ showHelp }) => ({ showHelp: !showHelp }));
  };

  handleOutsideHelpClick = (e: MouseEvent) => {
    const helpWrap = this.helpWrapRef.current;

    if (!this.state.showHelp || !helpWrap) {
      return;
    }

    if (!helpWrap.contains(e.target as Node)) {
      this.setState({ showHelp: false });
    }
  };

  render() {
    const { mode, email, error, lookingUp, showHelp } = this.state;
    const isPasswordStep = mode === "login" || mode === "signUp";

    return (
      <div className="auth-page" id="login">
        <div className="auth-blob one" aria-hidden="true">
          <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#2E2437"
              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
            />
            <path
              fill="#2E2437"
              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
            />
          </svg>
        </div>
        <div className="auth-blob two" aria-hidden="true">
          <svg viewBox="0 0 200 250" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#2E2437"
              d="M175.67,131.46C185.77,91.05,149.4,97.11,129.19,85S113.86,55.87,78.68,52.66,20,70.8,14,101.15,14.24,173.8,60.49,184,165.56,171.88,175.67,131.46Z"
            />
            <path
              fill="#2E2437"
              d="M137.92,47.6c-5.66,4.09-16.39,18.52-7.47,26.74s24.3,5.46,31.37-7.73c6.33-11.78-1.85-18.29-7.66-20.88S141.17,45.31,137.92,47.6Z"
            />
          </svg>
        </div>

        <header className="auth-header">
          <a className="brand" href="/" aria-label="42courses home">
            <img
              src={require("../../../../assets/images/landings/home-logo.svg")}
              alt="42courses"
            />
          </a>
          <div className="help-wrap" ref={this.helpWrapRef}>
            <button
              type="button"
              className="help-link"
              aria-expanded={showHelp}
              aria-controls="login-help-pop"
              onClick={this.toggleHelp}
            >
              Need a hand?
            </button>
            <div
              className="help-pop"
              id="login-help-pop"
              role="dialog"
              aria-label="Need a hand?"
              hidden={!showHelp}
            >
              <p>
                If you are stuck or need a hand, reach out to us at{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>
                .
              </p>
            </div>
          </div>
        </header>

        <main className="auth-main">
          <section
            className="signin-card"
            id="auth-card"
            aria-labelledby="signin-title"
          >
            <div className="signin-head">
              <p className="signin-greeting">Hello there</p>
              <h1 className="signin-title" id="signin-title">
                Sign in or join.
              </h1>
              {mode === "email" && (
                <p className="signin-sub">
                  Pop your email in and we will take it from there.
                </p>
              )}
              {mode === "login" && (
                <p className="signin-sub">Welcome back. Enter your password.</p>
              )}
              {mode === "signUp" && (
                <p className="signin-sub">
                  Looks like you are new. Pick a password to create your
                  account.
                </p>
              )}
            </div>

            {error && mode !== "email" && (
              <p className="status-banner error" role="alert">
                {error}
              </p>
            )}

            <div
              className="signin-social"
              role="group"
              aria-label="Sign in or sign up with a social account"
            >
              <a
                className="btn-social click-busy"
                href="/oauth/linkedin"
                aria-label="Continue with LinkedIn"
              >
                <LinkedinIcon />
                <span className="label">LinkedIn</span>
              </a>
              <a
                className="btn-social click-busy"
                href="/oauth/google"
                aria-label="Continue with Google"
              >
                <GoogleIcon />
                <span className="label">Google</span>
              </a>
              <a
                className="btn-social click-busy"
                href="/oauth/facebook"
                aria-label="Continue with Facebook"
              >
                <FacebookIcon />
                <span className="label">Facebook</span>
              </a>
            </div>

            <p className="signin-divider">
              <span>or, old school</span>
            </p>

            <div className="forms">
              {mode === "email" && (
                <form className="ui form" onSubmit={this.handleEmailLookup}>
                  <div className="field">
                    <div className="ui icon action input">
                      <input
                        type="email"
                        id="session_email_lookup"
                        name="email"
                        placeholder="Email"
                        autoComplete="email"
                        value={email}
                        onChange={this.setEmail}
                        aria-invalid={Boolean(error)}
                        aria-describedby={
                          error ? "session_email_lookup_error" : undefined
                        }
                      />
                    </div>
                    {error && (
                      <div
                        className="field-validation"
                        id="session_email_lookup_error"
                        role="alert"
                      >
                        {error}
                      </div>
                    )}
                  </div>
                  <div className="action">
                    <button
                      type="submit"
                      className={`ui button ${lookingUp ? "loading" : ""}`}
                      disabled={lookingUp}
                    >
                      Continue
                    </button>
                  </div>
                </form>
              )}

              {isPasswordStep && (
                <div className="email-tag">
                  <span className="email">{email}</span>
                  <button
                    type="button"
                    className="edit"
                    onClick={this.handleEditEmail}
                  >
                    Edit
                  </button>
                </div>
              )}

              {mode === "signUp" && (
                <SignUpForm initialEmail={email} setError={this.setError} />
              )}
              {mode === "login" && (
                <LoginForm initialEmail={email} setError={this.setError} />
              )}
            </div>

            <p className="signin-foot">
              <a href="/terms">Terms</a> &nbsp;&middot;&nbsp;{" "}
              <a href="/privacy">Privacy</a>
            </p>
          </section>
        </main>

        <footer className="auth-footer">
          Made with care in London. &nbsp;&middot;&nbsp;{" "}
          <a href="/">Back to 42courses</a>
        </footer>
      </div>
    );
  }
}
export default Login;
